import { useQuery, useMutation } from '@apollo/client';
import { graphql } from '../../__generated__';
import {
  Dialog,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  CircularProgress,
  DialogContent,
  Typography,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';

export const getTenantNamesQuery = graphql(/* GraphQL */ `
  query GetTenantNames {
    tenants {
      id
      name
    }
  }
`);

export const setAdminTenantMutation = graphql(/* GraphQL */ `
  mutation SetAdminAgentTenant($id: ID!, $tenantId: String!) {
    setAdminAgentTenant(id: $id, tenantId: $tenantId) {
      code
      message
      success
    }
  }
`);

export type TenantChooserDialogProps = {
  open: boolean;
  onNewSelected: (value: string) => void;
  onClose: () => void;
  tenantId: string;
  agentId: string;
};

const TenantChooserDialog = ({
  open,
  onClose,
  onNewSelected,
  agentId,
  tenantId,
}: TenantChooserDialogProps) => {
  const { data, loading, error } = useQuery(getTenantNamesQuery);
  const [setAdminTenant, { loading: mutating, error: mutationError }] =
    useMutation(setAdminTenantMutation, { refetchQueries: ['Settings'] });

  const handleClose = () => {
    onClose();
  };

  const handleListItemClick = async (value?: string) => {
    if (value && value !== tenantId && agentId) {
      const variables = {
        id: agentId,
        tenantId: value,
      };
      const result = await setAdminTenant({ variables });
      if (result.data?.setAdminAgentTenant?.success) {
        onNewSelected(value);
        return;
      }
    }
    onClose();
  };

  const tenants = data?.tenants || [];
  const dataLoading = loading || mutating;
  const errorMessage = error?.message || mutationError?.message;

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Select Platform Client</DialogTitle>
      {dataLoading && <CircularProgress />}
      {errorMessage && (
        <DialogContent>
          <Typography sx={{ color: 'red' }}>{errorMessage}</Typography>
        </DialogContent>
      )}
      {!dataLoading && !errorMessage && open && (
        <List sx={{ pt: 0 }}>
          {tenants.map((tenant) => (
            <ListItem disableGutters key={tenant?.id}>
              <ListItemButton
                selected={tenant?.id === tenantId}
                onClick={() => handleListItemClick(tenant?.id)}
                key={tenant?.id}
              >
                <ListItemAvatar>
                  <Avatar>
                    <BusinessIcon />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText primary={tenant?.name} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Dialog>
  );
};

export default TenantChooserDialog;
